import React from 'react';
import { getClassIcon } from 'global/utils/election/subscriberClassHelper';
import { ChevronDown, ChevronUp } from '@insurancemenu/imenu-components-lib/dist/icons';
import {
  DropdownOptionType,
  OptionsAlignmentTypes,
  AlignmentTypes,
} from '@insurancemenu/imenu-components-lib/dist/types';
import { Dropdown, Tooltip } from '@insurancemenu/imenu-components-lib';
import { EeClassWithValidation } from 'types/electionService';
import {
  ClassSectionStyled,
  ClassButtonStyled,
  SelectedClassStyled,
  ClassIconStyled,
  ClassNameStyled,
  ClassOptionLabelStyled,
} from './styles';
import {
  SUB_CLASS_DROPDOWN_TEST_ID,
  SUB_NOT_EDITABLE_CLASS_TOOLTIP_TEST_ID,
  SUB_SELECTED_CLASS_TEST_ID,
} from '../../constants';

interface SubscriberClassDropdownProps {
  employeeClasses: EeClassWithValidation[];
  showFullClassOptions: boolean;
  employeeClass: string;
  updateClass?: (className: string) => void;
}

interface SelectedClassProps {
  eeClassName: string;
  employeeClasses: EeClassWithValidation[];
}

interface ClassDropdownProps {
  employeeClasses: EeClassWithValidation[];
  updateClass?: (name: string) => void;
  showFullClassOptions: boolean;
}

const SelectedClass: React.FC<SelectedClassProps> = ({ eeClassName, employeeClasses }) => {
  const type = employeeClasses.find(({ name }) => name === eeClassName)?.type || 'CUSTOM';

  return (
    <SelectedClassStyled data-test-id={SUB_SELECTED_CLASS_TEST_ID}>
      <ClassIconStyled>{getClassIcon(type, eeClassName)} </ClassIconStyled>
      <ClassNameStyled>{eeClassName}</ClassNameStyled>
    </SelectedClassStyled>
  );
};

const ClassDropdown: React.FC<ClassDropdownProps> = ({ employeeClasses, showFullClassOptions, updateClass }) => {
  const noSelectionOption: DropdownOptionType = {
    value: '',
    label: '--No Selection--',
    onOptionClick: (): void => {
      updateClass?.('');
    },
    'data-test-id': 'sub-class-dropdown-no-selection',
  };

  const employeeClassOptions: DropdownOptionType[] = employeeClasses.map(({ name, type }): DropdownOptionType => {
    return {
      value: name,
      icon: () => getClassIcon(type, name),
      label: <ClassOptionLabelStyled>{name}</ClassOptionLabelStyled>,
      onOptionClick: (): void => {
        updateClass?.(name);
      },
      'data-test-id': `sub-class-dropdown-${name}`,
    };
  });

  return (
    <Dropdown
      name="subscriber-class-dropdown"
      options={showFullClassOptions ? [noSelectionOption, ...employeeClassOptions] : employeeClassOptions}
      optionsAlignment={OptionsAlignmentTypes.BOTTOM_RIGHT}
      optionsWidth="25rem"
      data-test-id={SUB_CLASS_DROPDOWN_TEST_ID}
      closedElement={
        <ClassButtonStyled
          color="primary"
          type="text"
          uppercase
          rightIconProp={<ChevronDown />}
          disabled={!updateClass}
        >
          Class
        </ClassButtonStyled>
      }
      openedElement={
        <ClassButtonStyled color="primary" type="text" uppercase rightIconProp={<ChevronUp />} disabled={!updateClass}>
          Class
        </ClassButtonStyled>
      }
    />
  );
};

const SubscriberClassDropdown: React.FC<SubscriberClassDropdownProps> = ({
  employeeClasses,
  showFullClassOptions,
  employeeClass,
  updateClass,
}) => {
  return (
    <ClassSectionStyled>
      {updateClass ? (
        <>
          <ClassDropdown
            employeeClasses={employeeClasses}
            showFullClassOptions={showFullClassOptions}
            updateClass={updateClass}
          />
          {employeeClass && <SelectedClass employeeClasses={employeeClasses} eeClassName={employeeClass} />}
        </>
      ) : (
        <Tooltip
          alignment={AlignmentTypes.BOTTOM_START}
          label="Contact employer admin if incorrect"
          data-test-id={SUB_NOT_EDITABLE_CLASS_TOOLTIP_TEST_ID}
        >
          <ClassDropdown
            employeeClasses={employeeClasses}
            updateClass={updateClass}
            showFullClassOptions={showFullClassOptions}
          />
          {employeeClass && <SelectedClass employeeClasses={employeeClasses} eeClassName={employeeClass} />}
        </Tooltip>
      )}
    </ClassSectionStyled>
  );
};

export default SubscriberClassDropdown;
